import { logger } from '@pkg/logger';
import { defineNuxtPlugin } from 'nuxt/app';
import { ampli } from '@pkg/analytics-helper/src/ampli';
import { ExtendAmplitudeEvents } from '@pkg/analytics-helper/src/amplitude/extendAmplitudeEvents';

export default defineNuxtPlugin((_nuxtApp) => {
  const { public: { amplitude: amplitudeConfig } } = useRuntimeConfig();

  let ampliReadyResolver: () => void;
  const ampliReadyPromise = new Promise<void>((resolve) => {
    ampliReadyResolver = resolve;
  });

  _nuxtApp.provide('ampliReady', ampliReadyPromise);

  const createAmplitudeNoConsent = async(isConsentGiven: boolean) => {
    try {
      await ampli.load({
        client: {
          apiKey: amplitudeConfig.projectId,
          configuration: {
            autocapture: {
              elementInteractions: {
                cssSelectorAllowlist: [
                  '.amp-auto-track',
                ],
              },
            },
            defaultTracking: true,
            optOut: !isConsentGiven,
            identityStorage: isConsentGiven ? 'cookie' : 'none',
          },
        },
      });
      ampli.client.add(new ExtendAmplitudeEvents());
      ampli?.client?.setOptOut(!isConsentGiven);
      ampliReadyResolver(); // Resolve the promise
    } catch (e) {
      logger.error('[plugin] - Error loading ampli: ', e);
    };
  };

  let isInitialized = false;

  const amplitudeActions = [
    {
      pattern: 'store.toogoodtogo.com/login',
      action: () => {
        try {
          ampli.track({
            event_type: 'mystore_login',
          });
        } catch (error) {
          logger.error('[plugin] - Error tracking mystore_login: ', error);
        }
      },
    },
    {
      pattern: 'store.toogoodtogo.com/onboarding',
      action: () => {
        try {
          ampli.track({
            event_type: 'single_sign_up',
          });
        } catch (error) {
          logger.error('[plugin] - Error tracking single_sign_up: ', error);
        }
      },
    },
  ];
  const analyticsClickFilterLayer = (event: any) => {
    const target = event.target.closest('a');
    if (!target || !target.href) { return; }
    const matchedAction = amplitudeActions.find(({ pattern }) => target.href.includes(pattern));
    if (matchedAction) {
      matchedAction.action();
    }
  };

  document?.addEventListener('click', analyticsClickFilterLayer, { passive: true, capture: true });
  document?.removeEventListener('click', analyticsClickFilterLayer);

  const updateAnalyticsConsent = (isConsentGiven: boolean) => {
    if (!isInitialized) {
      createAmplitudeNoConsent(isConsentGiven);
      isInitialized = true;
    } else if (!isConsentGiven) {
      ampli?.client?.setOptOut(true);
    } else {
      ampli?.client?.setOptOut(false);
    }
  };

  return {
    provide: {
      updateAnalyticsConsent,
    },
  };
});
declare module '#app' {
  interface NuxtApp {
    $updateAnalyticsConsent: (isConsentGiven: BooleanConstructor) => Promise<void>;
  }
}
